import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal, { Overline } from "@/components/Reveal";

const IMG =
  "https://images.unsplash.com/photo-1758691736545-5c33b6255dca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxleGVjdXRpdmUlMjBsZWFkZXIlMjBwcmVzZW50YXRpb24lMjBib2FyZHJvb20lMjBtZWV0aW5nJTIwc3BlYWtlciUyMGVkaXRvcmlhbHxlbnwwfHx8fDE3OTI5NzEzMzR8MA&ixlib=rb-4.1.0&q=85";

const CEO_POINTS = [
  "Sound as clear and decisive as your strategy deserves in town halls and all-hands meetings",
  "Hold the room through tough Q&A without rushing, hedging, or losing composure",
  "Present with authority to boards, analysts, and your own leadership team",
  "Maintain vocal stamina across the longest days of back-to-back communication",
];

const AUDIENCES = [
  {
    name: "Founders & Entrepreneurs",
    line: "Investor pitches, hard Q&A, and the long fundraising stretch.",
  },
  {
    name: "Sales & Business Development Leaders",
    line: "Discovery calls, demos, and negotiations where tone closes the deal.",
  },
  {
    name: "Attorneys & Legal Counsel",
    line: "Openings, closings, and client counsel delivered with authority.",
  },
  {
    name: "Investor Relations & Communications Leaders",
    line: "Earnings calls, analyst days, and the voice behind the numbers.",
  },
  {
    name: "Keynote & Conference Speakers",
    line: "Command the stage and hold a room's attention to the last word.",
  },
];

export default function Audiences() {
  return (
    <section id="who-i-work-with" data-testid="audiences-section" className="bg-[#F7F5F1] py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        <Reveal className="max-w-2xl">
          <Overline>Who I Work With</Overline>
          <h2 className="mt-6 font-serif text-3xl leading-[1.2] tracking-tight text-[#26282E] sm:text-4xl">
            Leaders whose words carry <em className="text-[#2F5D8C]">weight.</em>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#5B5E66] sm:text-lg">
            Different rooms, same demand: a voice that stays clear, steady, and credible under pressure.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            data-testid="executives-feature"
            className="card-shadow-lg mt-14 grid overflow-hidden rounded-md bg-white lg:grid-cols-2"
          >
            <div className="relative min-h-[280px]">
              <img
                src={IMG}
                alt="Executive leader addressing a group of professionals in a muted boardroom setting"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#26282E]/50 to-transparent" />
            </div>
            <div className="p-8 sm:p-10 lg:p-12">
              <span className="font-mono text-[11px] tracking-[0.2em] text-[#2F5D8C]">01</span>
              <h3 className="mt-4 font-serif text-2xl leading-snug text-[#26282E] sm:text-3xl">
                CEOs &amp; C-Suite Executives
              </h3>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8A8D94]">
                Leading from the front of the room
              </p>
              <p className="mt-5 text-[15px] leading-relaxed text-[#5B5E66]">
                Your audience reads your tone before they process your words. I help you carry the room — and
                sound credible, no matter what situation you're facing.
              </p>
              <ul className="mt-6 space-y-3">
                {CEO_POINTS.map((point, i) => (
                  <li key={i} data-testid={`executive-point-${i + 1}`} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-[#2F5D8C]" />
                    <span className="text-sm leading-relaxed text-[#26282E]/90">{point}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/services"
                data-testid="executives-cta-link"
                className="group mt-8 inline-flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2F5D8C] transition-colors hover:text-[#254A6E]"
              >
                Explore Services
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>

        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {AUDIENCES.map((a, i) => (
            <Reveal key={a.name} delay={i * 0.07} className="h-full">
              <div
                data-testid={`audience-card-${i + 2}`}
                className="card-shadow group h-full rounded-md bg-white p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_14px_44px_rgba(38,40,46,0.12)] sm:p-8"
              >
                <span className="font-mono text-[11px] tracking-[0.2em] text-[#2F5D8C]">
                  {String(i + 2).padStart(2, "0")}
                </span>
                <h3 className="mt-5 font-serif text-xl leading-snug text-[#26282E]">{a.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#5B5E66]">{a.line}</p>
                <div className="accent-rule mt-6 w-10 transition-all duration-500 group-hover:w-full" />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
